import { ReactNode, useReducer, createContext } from 'react';

export enum ActiveIndex {
  Top = 100,
  Setting = 200,
  SummaryChat = 300,
  OptimizeChat = 400,
  ScheduleChat = 500,
  ScheduleChatSelectUser = 501,
  ScheduleChatMessage = 502,
  RecommendChat = 600,
  Conversation = 900,
}

export interface State {
  chatPopoverAnchor: HTMLElement | null;
  activeIndex: ActiveIndex;
  isAvailable: boolean;
  isShowOnboard: boolean;
  isShowUpgrade: boolean;
}

const initialState: State = {
  chatPopoverAnchor: null,
  activeIndex: ActiveIndex.Top,
  isAvailable: false,
  isShowOnboard: false,
  isShowUpgrade: false,
};

export const setActiveIndex = (activeIndex: ActiveIndex) =>
  ({
    type: 'SET_ACTIVE_INDEX',
    activeIndex,
  } as const);
export const setChatPopoverAnchor = (chatPopoverAnchor: HTMLElement | null) =>
  ({
    type: 'SET_CHAT_POPOVER_ANCHOR',
    chatPopoverAnchor,
  } as const);
export const setIsAvailable = (isAvailable: boolean) =>
  ({
    type: 'SET_IS_AVAILABLE',
    isAvailable,
  } as const);
export const setIsShowOnboard = (isShowOnboard: boolean) =>
  ({
    type: 'SET_IS_SHOW_ONBOARD',
    isShowOnboard,
  } as const);
export const setIsShowUpgrade = (isShowUpgrade: boolean) =>
  ({
    type: 'SET_IS_SHOW_UPGRADE',
    isShowUpgrade,
  } as const);

export type Action =
  | ReturnType<typeof setActiveIndex>
  | ReturnType<typeof setIsAvailable>
  | ReturnType<typeof setIsShowOnboard>
  | ReturnType<typeof setIsShowUpgrade>
  | ReturnType<typeof setChatPopoverAnchor>;

const reducer = (state: State = initialState, action: Action) => {
  switch (action.type) {
    case 'SET_ACTIVE_INDEX':
      return {
        ...state,
        activeIndex: action.activeIndex,
      };
    case 'SET_CHAT_POPOVER_ANCHOR':
      return {
        ...state,
        chatPopoverAnchor: action.chatPopoverAnchor,
      };
    case 'SET_IS_AVAILABLE':
      return {
        ...state,
        isAvailable: action.isAvailable,
      };
    case 'SET_IS_SHOW_ONBOARD':
      return {
        ...state,
        isShowOnboard: action.isShowOnboard,
      };
    case 'SET_IS_SHOW_UPGRADE':
      return {
        ...state,
        isShowUpgrade: action.isShowUpgrade,
      };
    default:
      return state;
  }
};

const AssistantWidgetContext = createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: initialState,
  dispatch: () => {},
});

const AssistantWidgetProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <AssistantWidgetContext.Provider value={{ state, dispatch }}>
      {children}
    </AssistantWidgetContext.Provider>
  );
};

export { AssistantWidgetContext, AssistantWidgetProvider };
